/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { FunctionComponent, useMemo } from 'react';
import {
  Button, ButtonSize, Highlights, Image, Badge, BadgeColor, Link,
} from '@doveit/bricks';

import ImagePlaceholder from './ImagePlaceholder';
import * as styles from './PropertyCard.style';
import { URLObject } from '../../utils/links';

export * from './PropertyCard.skeleton';

export interface PropertyCardProps {
  title: string,
  suburb?: string,
  price: string,
  onClick?: VoidFunction,
  urlObject?: URLObject,
  features?: string[],
  image: string,
  banner?: string,
  newBuilding?: boolean,
  description?: string,
  label?: string,
  ariaLabel?: string,
  buildingSiteLogo?: {
    src: string,
    alt: string,
  },
  promoted?: boolean,
}

export const PropertyCard: FunctionComponent<PropertyCardProps> = ({
  title,
  suburb,
  price,
  onClick,
  urlObject,
  image,
  banner,
  newBuilding,
  description,
  buildingSiteLogo,
  promoted,
  features = [],
  ariaLabel = 'Clicca per vedere i dettagli immobile',
  label = 'Vedi dettagli immobile',
}) => {
  const badges = useMemo(
    () => [
      promoted && 'Premium',
      newBuilding && 'Nuova costruzione',
    ].filter((i) => i),
    [newBuilding, promoted],
  );

  return (
    <article
      css={styles.basic}
      $interactive={!!onClick}
    >
      {onClick && (
        <div
          css={styles.overlayLink}
          onClick={onClick}
          title={ariaLabel}
          aria-label="property-card-overlay"
          role="button"
        />
      )}
      <div css={styles.content}>
        <div css={styles.info}>
          <h2 css={styles.title}>
            <Link
              aria-label="anchor-link"
              href={urlObject?.as}
            >
              {title}
            </Link>
          </h2>
          {suburb && (
            <div css={styles.suburb}>{suburb}</div>
          )}
          {features && (
            <div css={styles.highlights}>
              <Highlights items={features} />
            </div>
          )}
          <div css={styles.price}>
            {price}
            {buildingSiteLogo && (
              <div css={styles.buildingSiteLogo}>
                <Image
                  src={buildingSiteLogo.src}
                  alt={buildingSiteLogo.alt}
                  loading="lazy"
                />
              </div>
            )}
          </div>
          {description && (
            <p css={styles.description}>
              {description}
            </p>
          )}
          {onClick && (
            <div css={styles.actions}>
              <Button
                label={label}
                size={ButtonSize.SMALL}
                onClick={onClick}
                aria-label={ariaLabel}
              />
            </div>
          )}
        </div>
        <div css={styles.preview}>
          <div css={styles.previewWrapper}>
            {badges.length > 0 && (
              <div css={styles.badges}>
                {badges.map((badge, i) => (
                  <Badge
                    key={badge}
                    text={badge}
                    color={i === 0 ? BadgeColor.PRIMARY : BadgeColor.NEUTRAL}
                  />
                ))}
              </div>
            )}
            {banner && (
              <div
                css={styles.banner}
                data-ref="property-card-banner"
              >
                {banner}
              </div>
            )}
            {image && (
              <div css={styles.image}>
                <Image
                  src={image}
                  alt={`Anteprima ${title}`}
                  loading="lazy"
                />
              </div>
            )}
            {!image && (
              <div
                css={styles.placeholder}
                data-ref="property-card-placeholder"
              >
                <ImagePlaceholder />
              </div>
            )}
          </div>
          {onClick && (
            <div css={styles.mobileActions}>
              <Button
                label={label}
                size={ButtonSize.SMALL}
                expanded
                onClick={onClick}
                aria-label={ariaLabel}
              />
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

export default PropertyCard;
